import { useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronUp, Users, Share2, X } from "lucide-react";
import { useContextMenu } from "../../contexts/RightClickMenuContext";
import { getSharedWithMe, removeShare } from "../../../api/sharing";

const CONTENT_TYPE_MAP = {
  reports: "report",
  feeds: "custom_feed_view",
  graphs: "custom_graph_view",
  wiki: "wiki_case",
};

const SharedWithMeSection = ({ contentType, onSelect }) => {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSharedWithMe();
      const d = res?.data || {};
      if (contentType === "reports") setItems(d.reports || []);
      else if (contentType === "feeds") setItems(d.feed_views || []);
      else if (contentType === "graphs") setItems(d.graph_views || []);
      else if (contentType === "wiki") setItems(d.wiki_cases || []);
      else setItems([]);
    } catch (e) {
      console.error("Error loading shared items:", e);
      setItems([]);
    }
    setLoading(false);
  }, [contentType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener("sidebar:refresh-shared", handler);
    return () => window.removeEventListener("sidebar:refresh-shared", handler);
  }, [load]);

  const menuZone = "shared-" + contentType + "-item";
  useContextMenu(menuZone, [
    {
      label: "Remove",
      icon: X,
      variant: "danger",
      action: (payload) => {
        setPendingRemoveId(payload._shareKey);
      },
    },
  ]);

  const getDocId = (item) => item.document_id || item.report_id || "";
  const getKey = (item) =>
    item.document_id || item.report_id || item.company_name || "";
  const getName = (item) => {
    if (contentType === "reports") return item.title || "Untitled Report";
    if (contentType === "wiki") return item.company_name || "Unknown Company";
    return item.name || "Untitled";
  };
  const getOwner = (item) => item.owner_name || "Unknown";

  const confirmRemove = async (item) => {
    try {
      await removeShare(
        CONTENT_TYPE_MAP[contentType],
        getDocId(item),
        item.owner_uuid,
      );
      load();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-shared"));
    } catch (e) {
      console.error("Remove shared item failed:", e);
    } finally {
      setPendingRemoveId(null);
    }
  };

  if (items.length === 0 && !loading) return null;

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-2 py-1 text-[11px] font-normal uppercase tracking-wide text-mutedText hover:text-mainText transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 rounded-lg"
      >
        <div className="flex items-center gap-x-1.5">
          <Share2 className="h-3 w-3" strokeWidth={2} />
          <span>Shared with me</span>
          <span className="text-[10px] font-normal text-mutedText">
            ({items.length})
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="h-3 w-3" strokeWidth={2} />
        ) : (
          <ChevronDown className="h-3 w-3" strokeWidth={2} />
        )}
      </button>

      <div
        className={[
          "overflow-hidden transition-all duration-200 ease-in-out",
          expanded ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0",
        ].join(" ")}
      >
        <div className="flex flex-col gap-y-px mt-0.5">
          {items.map((item) => {
            const key = getKey(item);
            const isPendingRemove = pendingRemoveId === key;
            return (
              <div
                key={key}
                data-context-menu={menuZone}
                data-context-payload={JSON.stringify({
                  ...item,
                  _shareKey: key,
                })}
              >
                <button
                  onClick={() => onSelect?.(item)}
                  className="w-full text-left rounded-lg px-2 py-1.5 text-[12px] transition hover:bg-mainButtonBg/50 group cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                >
                  <div className="flex items-center gap-x-1.5 min-w-0">
                    <Users
                      className="h-3 w-3 shrink-0 text-mutedText"
                      strokeWidth={2}
                    />
                    <span className="truncate text-mutedText group-hover:text-mainText transition-colors duration-200">
                      {getName(item)}
                    </span>
                  </div>
                  <p className="text-[10px] text-mutedText ml-[18px] truncate">
                    from {getOwner(item)}
                  </p>
                </button>

                <div
                  className={[
                    "overflow-hidden transition-all duration-200 ease-in-out",
                    isPendingRemove
                      ? "max-h-[80px] opacity-100 mt-0.5 mb-0.5"
                      : "max-h-0 opacity-0",
                  ].join(" ")}
                >
                  {isPendingRemove && (
                    <div className="flex items-center gap-x-2 py-1 px-1">
                      <span className="text-[11px] text-mutedText">
                        Remove?
                      </span>
                      <button
                        onClick={() => setPendingRemoveId(null)}
                        className="text-[11px] px-2 py-0.5 rounded-lg border border-btnBorder bg-transparent text-mainText hover:bg-btnHover transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => confirmRemove(item)}
                        className="text-[11px] px-2 py-0.5 rounded-lg border border-errorText/30 bg-transparent text-errorText hover:bg-errorText/10 transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-errorText/40"
                      >
                        Confirm
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SharedWithMeSection;
